"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("./util");
const logger_1 = require("./logger");
const negotiator_1 = require("./negotiator");
const enums_1 = require("./enums");
const baseconnection_1 = require("./baseconnection");
const encodingQueue_1 = require("./encodingQueue");
/**
 * Wraps a DataChannel between two Peers.
 */
class DataConnection extends baseconnection_1.BaseConnection {
    constructor(peerId, provider, options) {
        super(peerId, provider, options);
        this.stringify = JSON.stringify;
        this.parse = JSON.parse;
        this._buffer = [];
        this._bufferSize = 0;
        this._buffering = false;
        this._chunkedData = {};
        this._encodingQueue = new encodingQueue_1.EncodingQueue();
        this.connectionId =
            this.options.connectionId || DataConnection.ID_PREFIX + util_1.util.randomToken();
        this.label = this.options.label || this.connectionId;
        this.serialization = this.options.serialization || enums_1.SerializationType.Binary;
        this.reliable = !!this.options.reliable;
        this._encodingQueue.on('done', (ab) => {
            this._bufferedSend(ab);
        });
        this._encodingQueue.on('error', () => {
            logger_1.default.error(`DC#${this.connectionId}: Error occured in encoding from blob to arraybuffer, close DC`);
            this.close();
        });
        this._negotiator = new negotiator_1.Negotiator(this);
        this._negotiator.startConnection(this.options._payload || {
            originator: true
        });
    }
    get type() {
        return enums_1.ConnectionType.Data;
    }
    get dataChannel() {
        return this._dc;
    }
    get bufferSize() { return this._bufferSize; }
    /** Called by the Negotiator when the DataChannel is ready. */
    initialize(dc) {
        this._dc = dc;
        this._configureDataChannel();
    }
    _configureDataChannel() {
        if (!util_1.util.supports.binaryBlob || util_1.util.supports.reliable) {
            this.dataChannel.binaryType = "arraybuffer";
        }
        this.dataChannel.onopen = () => {
            logger_1.default.log(`DC#${this.connectionId} dc connection success`);
            this._open = true;
            this.emit(enums_1.ConnectionEventType.Open);
        };
        this.dataChannel.onmessage = (e) => {
            logger_1.default.log(`DC#${this.connectionId} dc onmessage:`, e.data);
            this._handleDataMessage(e);
        };
        this.dataChannel.onclose = () => {
            logger_1.default.log(`DC#${this.connectionId} dc closed for:`, this.peer);
            this.close();
        };
    }
    // Handles a DataChannel message.
    _handleDataMessage({ data }) {
        const datatype = data.constructor;
        const isBinarySerialization = this.serialization === enums_1.SerializationType.Binary ||
            this.serialization === enums_1.SerializationType.BinaryUTF8;
        let deserializedData = data;
        if (isBinarySerialization) {
            if (datatype === Blob) {
                // Datatype should never be blob
                util_1.util.blobToArrayBuffer(data, (ab) => {
                    const unpackedData = util_1.util.unpack(ab);
                    this.emit(enums_1.ConnectionEventType.Data, unpackedData);
                });
                return;
            }
            else if (datatype === ArrayBuffer) {
                deserializedData = util_1.util.unpack(data);
            }
            else if (datatype === String) {
                // String fallback for binary data for browsers that don't support binary yet
                const ab = util_1.util.binaryStringToArrayBuffer(data);
                deserializedData = util_1.util.unpack(ab);
            }
        }
        else if (this.serialization === enums_1.SerializationType.JSON) {
            deserializedData = this.parse(data);
        }
        // Check if we've chunked--if so, piece things back together.
        // We're guaranteed that this isn't 0.
        if (deserializedData.__peerData) {
            this._handleChunk(deserializedData);
            return;
        }
        super.emit(enums_1.ConnectionEventType.Data, deserializedData);
    }
    _handleChunk(data) {
        const id = data.__peerData;
        const chunkInfo = this._chunkedData[id] || {
            data: [],
            count: 0,
            total: data.total
        };
        chunkInfo.data[data.n] = data.data;
        chunkInfo.count++;
        this._chunkedData[id] = chunkInfo;
        if (chunkInfo.total === chunkInfo.count) {
            // Clean up before making the recursive call to `_handleDataMessage`.
            delete this._chunkedData[id];
            // We've received all the chunks--time to construct the complete data.
            const data = new Blob(chunkInfo.data);
            this._handleDataMessage({ data });
        }
    }
    /**
     * Exposed functionality for users.
     */
    /** Allows user to close connection. */
    close() {
        this._buffer = [];
        this._bufferSize = 0;
        this._chunkedData = {};
        if (this._negotiator) {
            this._negotiator.cleanup();
            this._negotiator = null;
        }
        if (this.provider) {
            this.provider._removeConnection(this);
            this.provider = null;
        }
        if (this.dataChannel) {
            this.dataChannel.onopen = null;
            this.dataChannel.onmessage = null;
            this.dataChannel.onclose = null;
            this._dc = null;
        }
        if (this._encodingQueue) {
            this._encodingQueue.destroy();
            this._encodingQueue.removeAllListeners();
            this._encodingQueue = null;
        }
        if (!this.open) {
            return;
        }
        this._open = false;
        super.emit(enums_1.ConnectionEventType.Close);
    }
    /** Allows user to send data. */
    send(data, chunked) {
        if (!this.open) {
            super.emit(enums_1.ConnectionEventType.Error, new Error("Connection is not open. You should listen for the `open` event before sending messages."));
            return;
        }
        if (this.serialization === enums_1.SerializationType.JSON) {
            this._bufferedSend(this.stringify(data));
        }
        else if (this.serialization === enums_1.SerializationType.Binary ||
            this.serialization === enums_1.SerializationType.BinaryUTF8) {
            const blob = util_1.util.pack(data);
            if (!chunked && blob.size > util_1.util.chunkedMTU) {
                this._sendChunks(blob);
                return;
            }
            if (!util_1.util.supports.binaryBlob) {
                // We only do this if we really need to (e.g. blobs are not supported),
                // because this conversion is costly.
                this._encodingQueue.enque(blob);
            }
            else {
                this._bufferedSend(blob);
            }
        }
        else {
            this._bufferedSend(data);
        }
    }
    _bufferedSend(msg) {
        if (this._buffering || !this._trySend(msg)) {
            this._buffer.push(msg);
            this._bufferSize = this._buffer.length;
        }
    }
    // Returns true if the send succeeds.
    _trySend(msg) {
        if (!this.open) {
            return false;
        }
        if (this.dataChannel.bufferedAmount > DataConnection.MAX_BUFFERED_AMOUNT) {
            this._buffering = true;
            setTimeout(() => {
                this._buffering = false;
                this._tryBuffer();
            }, 50);
            return false;
        }
        try {
            this.dataChannel.send(msg);
        }
        catch (e) {
            logger_1.default.error(`DC#:${this.connectionId} Error when sending:`, e);
            this._buffering = true;
            this.close();
            return false;
        }
        return true;
    }
    // Try to send the first message in the buffer.
    _tryBuffer() {
        if (!this.open) {
            return;
        }
        if (this._buffer.length === 0) {
            return;
        }
        const msg = this._buffer[0];
        if (this._trySend(msg)) {
            this._buffer.shift();
            this._bufferSize = this._buffer.length;
            this._tryBuffer();
        }
    }
    _sendChunks(blob) {
        const blobs = util_1.util.chunk(blob);
        logger_1.default.log(`DC#${this.connectionId} Try to send ${blobs.length} chunks...`);
        for (let blob of blobs) {
            this.send(blob, true);
        }
    }
    handleMessage(message) {
        const payload = message.payload;
        switch (message.type) {
            case enums_1.ServerMessageType.Answer:
                this._negotiator.handleSDP(message.type, payload.sdp);
                break;
            case enums_1.ServerMessageType.Candidate:
                this._negotiator.handleCandidate(payload.candidate);
                break;
            default:
                logger_1.default.warn("Unrecognized message type:", message.type, "from peer:", this.peer);
                break;
        }
    }
}
exports.DataConnection = DataConnection;
DataConnection.ID_PREFIX = "dc_";
DataConnection.MAX_BUFFERED_AMOUNT = 8 * 1024 * 1024;
